import { useEffect } from 'react';

/**
 * Chunk error detection and recovery utilities.
 *
 * Two-layer architecture for handling stale/broken chunk loads:
 *
 * Layer 1 (transparent): The `vite:preloadError` handler in main.tsx auto-reloads
 * before TanStack Router's error boundary fires. Users never see an error UI.
 *
 * Layer 2 (fallback): If the reload didn't help, or the error isn't from Vite's
 * preloader (e.g. CDN serving HTML for a 404'd chunk, Safari syntax error from
 * unsupported syntax on an old WebKit version), the error propagates to the route's
 * errorComponent which renders ChunkErrorFallback.
 */

/** Session storage key to prevent infinite reload loops. Shared across all error boundaries. */
export const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

/**
 * Detects whether an error is a chunk/module load failure.
 * These occur after a deployment when a device's browser tries to load a JS chunk
 * that no longer exists or receives non-JS content (e.g. an HTML error page),
 * or when the device's browser engine can't parse modern syntax in the bundle.
 *
 * Common error messages across browsers:
 * - Chrome: "Unexpected token '<'" or "Unexpected token '{'"
 * - Safari: "Unexpected token '<'" or "Unexpected identifier"
 * - Firefox: "expected expression, got '<'"
 * - All: "Failed to fetch dynamically imported module"
 * - All: "error loading dynamically imported module"
 * - All: "Importing a module script failed"
 */
export function isChunkLoadError(error: Error): boolean {
  const msg = error.message?.toLowerCase() ?? '';
  return (
    msg.includes('unexpected token') ||
    msg.includes('dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    // "failed to fetch" is intentionally broad — it catches dynamic import failures
    // from both deployment skew (404 on old chunk) and network issues. This means a
    // plain network timeout on a GraphQL request COULD trigger one gratuitous reload
    // if it happens to bubble up as a TypeError through TanStack Router's error boundary
    // (rare — Apollo catches its own network errors). The session storage guard limits
    // the damage to a single reload. Narrowing this to only dynamic import contexts is
    // not possible cross-browser (Chrome doesn't include the module URL in TypeError).
    // The vite:preloadError handler in main.tsx handles the common case before this
    // code runs — this is the fallback for non-Vite-preloader failures.
    msg.includes('failed to fetch') ||
    msg.includes("expected expression, got '<'")
  );
}

/**
 * Hook that triggers a one-time page reload when a chunk error is detected.
 * Uses sessionStorage to prevent infinite reload loops. If the reload already
 * happened and the error persists, the hook does nothing (letting the fallback UI show).
 */
export function useChunkErrorReload(isChunkError: boolean): void {
  useEffect(() => {
    if (isChunkError) {
      const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
      }
    }
  }, [isChunkError]);
}

/**
 * Fallback UI shown when a chunk load error persists after auto-reload.
 * Provides a manual "Refresh Now" button that clears the reload guard and tries again.
 */
export function ChunkErrorFallback() {
  return (
    <div role="alert" style={{ padding: 20, textAlign: 'center' }}>
      <h2>Update Required</h2>
      <p>A new version of the app is available. Please refresh to continue.</p>
      <button
        type="button"
        aria-label="Refresh the page to load the latest version"
        onClick={() => {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY);
          window.location.reload();
        }}
        style={{
          padding: '8px 16px',
          fontSize: 14,
          cursor: 'pointer',
          border: '1px solid #ccc',
          borderRadius: 4,
          background: '#fff',
        }}
      >
        Refresh Now
      </button>
    </div>
  );
}
